import { FC, useEffect } from 'react';

import { CandidatePersonalDetails, CandidatePersonalDetailsFormModel } from 'store/candidate/types';
import { GenderOptions, formatDate } from 'core';
import { ReadonlyField, BcCheckbox } from 'components';
import styled from '@emotion/styled';

import { FormFields } from '@britishcouncil/react-forms';
import { Divider, Quote, RadioGroup, SelectField, TextField } from '@britishcouncil/react-solas-ors3';


export const CandidateFormFields: FC<{
  fields: FormFields<CandidatePersonalDetailsFormModel>;
  candidate: CandidatePersonalDetails;
  countriesOptions: { id: string; name: string }[];
  familyRelationshipOptions: { id: string; name: string }[];
  canEditUciEdexcel?: boolean;
  canEditUciAqa?: boolean;
}> = ({ fields, candidate, countriesOptions, familyRelationshipOptions, canEditUciEdexcel, canEditUciAqa }) => {
  useEffect(() => {
    if (!fields.hasFamilyName.value) {
      fields.familyName.onChange(null);
    } else if (!fields.familyName.value) {
      fields.familyName.onChange("");
    }
  }, [fields.hasFamilyName.value, fields.familyName, fields.familyName.value]);

  return (<>
    <TextField label="First Name *" {...fields.otherNames} showColon={false} />
    <TextField label="Last Name" {...fields.familyName} showColon={false} disabled={!fields.hasFamilyName.value} value={fields.familyName.value ?? ''} />
    <S.CheckBoxContainer>
      <BcCheckbox {
        ...fields.hasFamilyName}
        value={!fields.hasFamilyName.value}
        onChange={(val) => fields.hasFamilyName.onChange(!val)}>
        I do not have a last name
      </BcCheckbox>
    </S.CheckBoxContainer>

    {!candidate.isChild && (
      <>
        <ReadonlyField label="Email">{candidate.email}</ReadonlyField>
        <Quote title="" intent="info">
          You are not allowed to edit Email field. To change those fields please contact nearest British Council office.
        </Quote>
      </>
    )}

    <ReadonlyField label="Date of Birth">{formatDate(candidate.dob)}</ReadonlyField>
    <Quote title="" intent="info">
      You are not allowed to edit Date of Birth field. To change those fields please contact nearest British Council
      office.
    </Quote>

    <label>Gender *</label>
    <RadioGroup {...fields.gender} radios={GenderOptions}></RadioGroup>
    <TextField label="Address Line 1 *" {...fields.address.streetAddress1} showColon={false} />
    <TextField label="Address Line 2" {...fields.address.streetAddress2} showColon={false} />
    <TextField label="Address Line 3" {...fields.address.streetAddress3} showColon={false} />
    <TextField label="Address Line 4" {...fields.address.streetAddress4} showColon={false} />
    <TextField label="City *" {...fields.address.city} showColon={false} />
    <TextField label="PostCode" {...fields.address.postCode} showColon={false} />
    <SelectField
      label="Country/Territory *"
      {...fields.address.countryId}
      options={countriesOptions}
      showColon={false}
    />

    {!candidate.isChild && (
      <>
        <TextField label="Alternative contact number *" {...fields.telephone} showColon={false} />
        <TextField label="Mobile number" {...fields.mobile} showColon={false} />
      </>
    )}

    {canEditUciEdexcel ? (
      <TextField label="UCI Edexcel" {...fields.uciEdexcel} showColon={false} />
    ) : (
      <>
        <ReadonlyField label="UCI Edexcel">{candidate.uciEdexcel}</ReadonlyField>
        <Quote title="" intent="info">
          You are not allowed to edit Edexcel UCI field. To change those fields please contact nearest British Council
          office.
        </Quote>
      </>
    )}

    {canEditUciAqa ? (
      <TextField label="UCI AQA" {...fields.uciAqa} showColon={false} />
    ) : (
      <>
        <ReadonlyField label="UCI AQA">{candidate.uciAqa}</ReadonlyField>
        <Quote title="" intent="info">
          You are not allowed to edit AQA UCI field. To change those fields please contact nearest British Council
          office.
        </Quote>
      </>
    )}

    {candidate.isChild && (
      <>
        <Divider />
        <h2>Parent/guardian contact details:</h2>
        <ReadonlyField label="First Name">{candidate.guardianContactDetails?.firstName}</ReadonlyField>
        <ReadonlyField label="Last Name">{candidate.guardianContactDetails?.lastName}</ReadonlyField>

        <ReadonlyField label="Email">{candidate.guardianContactDetails?.email}</ReadonlyField>
        <SelectField
          label="Relationship"
          {...fields.guardianContactDetails.familyRelationshipId}
          options={familyRelationshipOptions}
          showColon={false}
        />
        <ReadonlyField label="Date of Birth">{formatDate(candidate.guardianContactDetails?.dateOfBirth)}</ReadonlyField>
        <TextField
          label="Alternative contact number *"
          {...fields.guardianContactDetails.telephone}
          showColon={false}
        />
        <TextField label="Mobile number" {...fields.guardianContactDetails.mobile} showColon={false} />
      </>)
    }
  </>
  );
};

const S = {
  CheckBoxContainer: styled.div`
    .checkbox {
      label {
        padding: 0 !important;

        input {
          position: relative;
          top: 0;
          left: 0;
          margin-right: 32px;
          margin-left: 12px;
        }
      }
    }
  `
};

